<template>
  <!-- 모바일 뷰 -->
  <MobileHomeView v-if="isMobile" />

  <!-- 웹 뷰 -->
  <div v-else class="home" ref="container">
    <div class="sections-wrapper" :style="wrapperStyle">
      <HeroSection :class="{ 'is-active': currentSection === 0 }" />
      <PremiumSection
        :items="premiumItems"
        :is-active="currentSection === 1"
        :class="{ 'is-active': currentSection === 1 }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
        ref="premiumSection"
      />
      <CommunitySection
        :is-active="currentSection === 2"
        :class="{ 'is-active': currentSection === 2 }"
        @next-section="goToNextSection"
        @prev-section="goToPrevSection"
        ref="communitySection"
      />
      <SpecializationSection
        :class="{ 'is-active': currentSection === 3 }"
      />
      <UnitSection
        :is-active="currentSection === 4"
        :class="{ 'is-active': currentSection === 4 }"
      />
      <FooterSection
        :class="{ 'is-active': currentSection === 5 }"
      />
    </div>

    <!-- 스크롤 인디케이터 -->
    <div
      class="scroll-indicator"
      :class="{
        'is-light': currentSection === 0 || currentSection === 2,
        'is-hidden': currentSection === 5
      }"
    >
      <div class="scroll-text">
        <span>S</span>
        <span>C</span>
        <span>R</span>
        <span>O</span>
        <span>L</span>
        <span>L</span>
      </div>
      <div class="scroll-text down">
        <span>D</span>
        <span>O</span>
        <span>W</span>
        <span>N</span>
      </div>
      <div class="scroll-line">
        <span class="line-track"></span>
        <span class="line-fill"></span>
        <span class="line-dot"></span>
      </div>
    </div>
  </div>
</template>

<script>
import HeroSection from '@/components/web/home/HeroSection.vue'
import PremiumSection from '@/components/web/home/PremiumSection.vue'
import CommunitySection from '@/components/web/home/CommunitySection.vue'
import SpecializationSection from '@/components/web/home/SpecializationSection.vue'
import UnitSection from '@/components/web/home/UnitSection.vue'
import FooterSection from '@/components/web/home/FooterSection.vue'
import MobileHomeView from '@/views/mobile/MobileHomeView.vue'

export default {
  name: 'HomeView',
  components: {
    HeroSection,
    PremiumSection,
    CommunitySection,
    SpecializationSection,
    UnitSection,
    FooterSection,
    MobileHomeView
  },
  data() {
    return {
      isMobile: false,
      currentSection: 0,
      totalSections: 6,
      isScrolling: false,
      touchStartY: 0,
      premiumItems: [
        {
          eng: 'Location',
          title: '직주근접 신중심지',
          desc: '20조원이상 투자되는 SK하이닉스 신규 팹(FAB)\nM15X 인접 및 단지 앞 청주테크노폴리스를 비롯하여\n청주 일반 산업단지, 오창과학산업단지 등이\n인접한 직주 근접 생활중심지',
          image: 'location'
        },
        {
          eng: 'Transportation',
          title: '사통팔달 교통요지',
          desc: '단지 앞 청주2순환로, 중부고속도로 서청주IC,\nKTX 오송역, 청주공항 등\n다양한 쾌속교통 루트가 경유하는 교통요지',
          image: 'transportation'
        },
        {
          eng: 'Infrastructure',
          title: '원스톱 생활인프라',
          desc: '봉덕초, 송절중, 봉명고교 등\n도보 5분 학세권 형성 및 현대백화점, 롯데아울렛,\n파비뇽아울렛 등 쇼핑몰 인접',
          image: 'infrastructure'
        },
        {
          eng: 'Nature',
          title: '청정자연 조성',
          desc: '단지 앞 황톳길, 무심천공원,\n명심산근린공원을 품은 천혜의 환경이며\n1,817세대(예정) 대단지에 공원형 조경 조성',
          image: 'nature'
        }
      ]
    }
  },
  computed: {
    wrapperStyle() {
      return {
        transform: `translateY(-${this.currentSection * 100}vh)`
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)

    // 웹일 때만 스크롤 이벤트 등록
    if (!this.isMobile && this.$refs.container) {
      this.$refs.container.addEventListener('wheel', this.handleWheel, { passive: false })
      this.$refs.container.addEventListener('touchstart', this.handleTouchStart, { passive: true })
      this.$refs.container.addEventListener('touchend', this.handleTouchEnd, { passive: true })
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    if (this.$refs.container) {
      this.$refs.container.removeEventListener('wheel', this.handleWheel)
      this.$refs.container.removeEventListener('touchstart', this.handleTouchStart)
      this.$refs.container.removeEventListener('touchend', this.handleTouchEnd)
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    handleWheel(e) {
      e.preventDefault()
      if (this.isScrolling) return

      const direction = e.deltaY > 0 ? 'down' : 'up'

      // 프리미엄 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 1) {
        const premiumSection = this.$refs.premiumSection
        if (premiumSection) {
          premiumSection.handleScroll(direction)
        }
        return
      }

      // 커뮤니티 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 2) {
        const communitySection = this.$refs.communitySection
        if (communitySection) {
          communitySection.handleScroll(direction)
        }
        return
      }

      if (e.deltaY > 0 && this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      } else if (e.deltaY < 0 && this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
    handleTouchStart(e) {
      this.touchStartY = e.touches[0].clientY
    },
    handleTouchEnd(e) {
      if (this.isScrolling) return
      const touchEndY = e.changedTouches[0].clientY
      const diff = this.touchStartY - touchEndY
      const direction = diff > 0 ? 'down' : 'up'

      // 프리미엄 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 1) {
        const premiumSection = this.$refs.premiumSection
        if (premiumSection && Math.abs(diff) > 50) {
          premiumSection.handleScroll(direction)
        }
        return
      }

      // 커뮤니티 섹션에서는 내부 스크롤 처리
      if (this.currentSection === 2) {
        const communitySection = this.$refs.communitySection
        if (communitySection && Math.abs(diff) > 50) {
          communitySection.handleScroll(direction)
        }
        return
      }

      if (diff > 50 && this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      } else if (diff < -50 && this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
    scrollToSection(index) {
      this.isScrolling = true
      this.currentSection = index

      setTimeout(() => {
        this.isScrolling = false
      }, 1800)
    },
    goToNextSection() {
      if (this.currentSection < this.totalSections - 1) {
        this.scrollToSection(this.currentSection + 1)
      }
    },
    goToPrevSection() {
      if (this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    }
  }
}
</script>

<style scoped>
.home {
  height: 100vh;
  overflow: hidden;
}

.sections-wrapper {
  transition: transform 1.6s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

/* 스크롤 인디케이터 */
.scroll-indicator {
  position: fixed;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  transition: all 0.5s ease;
}

.scroll-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.scroll-text span {
  font-family: var(--font-paperlogy);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  color: var(--text-color);
  opacity: 0.4;
  transition: color 0.5s ease;
}

.scroll-indicator.is-light .scroll-text span {
  color: #fff;
}

.scroll-indicator.is-hidden {
  opacity: 0;
  pointer-events: none;
}

.scroll-text.down {
  margin-top: 8px;
}

.scroll-line {
  position: relative;
  width: 1px;
  height: 80px;
  margin-top: 16px;
  overflow: visible;
}

.line-track {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 100%;
  background: rgba(163, 32, 53, 0.2);
}

.line-fill {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 40px;
  background: linear-gradient(to bottom, transparent, var(--rayone-primary));
  animation: scrollLine 2.5s ease-in-out infinite;
}

.line-dot {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  background: var(--rayone-primary);
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(163, 32, 53, 0.6);
  animation: scrollDot 2.5s ease-in-out infinite;
}

@keyframes scrollLine {
  0% {
    top: -40px;
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    top: 80px;
    opacity: 0;
  }
}

@keyframes scrollDot {
  0% {
    top: -4px;
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    top: 116px;
    opacity: 0;
  }
}
</style>
